import React, { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Plus, X } from "lucide-react";
import { Capacitor } from '@capacitor/core';
import { scanBadge } from "@/api/base44Client";
import { runOnDeviceOcr } from "@/lib/onDeviceOcr";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import TapToFillBlocks from "./TapToFillBlocks";

/**
 * onBackImageCaptured / onOfflineBackCapture: the back of the same card,
 * task: two-sided business card capture (2026-09-08). Optional and
 * independent of the front: many cards put the contact details on the
 * back, so both are sent to the model in one call and read as one card.
 * Adding a back photo re-runs the scan with both images, the same as
 * tapping Rescan already re-runs it with just the front; a fresh front
 * upload on that second call orphans the first one in R2, the same
 * accepted cost documented in worker/routes/badge.ts.
 *
 * onOfflineOcrAttempted: task: Capacitor + on-device OCR before FS/TEC
 * (2026-09-08). Fired once on-device OCR genuinely completes (found
 * blocks or not), never on a plugin/infra failure, so the caller can
 * stamp badge_ocr_attempted_at the same way handleImageCaptured already
 * does for the online scan. Only ever reachable offline, on the native
 * app, front image only for v1 (see onDeviceOcr.js and this file's own
 * offline branch below): it never runs, and never replaces, the online
 * Anthropic scan when there is signal to reach it.
 */
export default function BadgeScanner({
  onDataExtracted,
  onImageCaptured,
  onBackImageCaptured,
  onDuplicateFound,
  onOfflineCapture,
  onOfflineBackCapture,
  onOfflineOcrAttempted,
}) {
  const fileInputRef = useRef(null);
  const backFileInputRef = useRef(null);
  // A second, capture-less input for the same front/back slot, task: camera
  // by default (2026-09-08). Setting `capture="environment"` on an input
  // makes iOS skip its own Photo Library / Take Photo / Choose File sheet
  // and jump straight into the camera, which is the whole point (95% of
  // captures are the camera), but it also removes the library option from
  // that input entirely, there is no in-camera "back to library" affordance
  // a web page can draw since the native camera UI is not web content. This
  // is the escape hatch: a plain input with no capture attribute, reused for
  // whichever slot was last tapped, which reliably reopens the full sheet.
  const libraryFileInputRef = useRef(null);
  const [libraryTarget, setLibraryTarget] = useState(null); // 'front' | 'back' | null
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingLabel, setProcessingLabel] = useState('');
  const [preview, setPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  // The compressed front File, kept (not just its preview) so adding a back
  // photo later can resend it alongside the back in one combined OCR call.
  const [frontFile, setFrontFile] = useState(null);
  const [lightbox, setLightbox] = useState(null); // null | 'front' | 'back'
  // ML Kit's TextBlock[], task: Capacitor + on-device OCR before FS/TEC
  // (2026-09-08). null until a genuinely offline, native capture produces
  // at least one block for the rep to tap-to-fill; TapToFillBlocks handles
  // an empty array the same as null (renders nothing).
  const [onDeviceBlocks, setOnDeviceBlocks] = useState(null);

  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = 1800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
        else { width = Math.round(width * MAX / height); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', 0.92);
    };
    img.src = url;
  });

  // Shared by a front-only scan and a front+back scan: one call now, same as
  // before task: two-sided business card capture, just optionally carrying
  // a second image. The server stores the photo(s), reads them together,
  // tidies the fields and checks for a duplicate.
  const runScan = async (frontCompressed, backCompressed) => {
    setIsProcessing(true);
    setProcessingLabel('Reading badge...');
    try {
      const result = await scanBadge(frontCompressed, backCompressed);

      onImageCaptured(result.badge_image_key);
      if (result.badge_back_image_key && onBackImageCaptured) {
        onBackImageCaptured(result.badge_back_image_key);
      }

      if (result.duplicate && onDuplicateFound) {
        onDuplicateFound(result.duplicate);
      }

      onDataExtracted(result.fields);
    } catch (err) {
      console.error(err);
      // Genuinely unreachable, not a badge reading failure: POST
      // /api/badge/scan also runs the OCR call, so there is no partial
      // "photo stored, reading failed" outcome to fall back on offline the
      // way there is for a real read failure below. The photo itself is not
      // lost either way: it stays with this capture and uploads once the
      // outbox actually reaches the server.
      if (err.status === 0) {
        if (onOfflineCapture) onOfflineCapture(frontCompressed);
        if (backCompressed && onOfflineBackCapture) onOfflineBackCapture(backCompressed);

        // On-device OCR, task: Capacitor + on-device OCR before FS/TEC
        // (2026-09-08). Native only (Capacitor.isNativePlatform() guards
        // it here too, redundant with onDeviceOcr.js's own check, since
        // this branch is also reachable from a plain mobile browser tab
        // with no signal), and front-of-card only for v1: a back photo
        // captured in the same offline attempt still just queues, typed
        // in by hand, the first thing cut if this slips (CLAUDE.md's own
        // cut order for this task). Never attempted when the online scan
        // above actually had signal to try, only inside this same offline
        // catch it always used.
        if (!backCompressed && Capacitor.isNativePlatform()) {
          setProcessingLabel('Reading text on device...');
          const result = await runOnDeviceOcr(frontCompressed);
          if (result) {
            if (onOfflineOcrAttempted) onOfflineOcrAttempted();
            if (result.blocks && result.blocks.length > 0) {
              setOnDeviceBlocks(result.blocks);
              toast.warning("You're offline. Tap the text below to fill in fields.");
            } else {
              toast.warning("You're offline. No legible text was found; type the details in by hand.");
            }
          } else {
            toast.warning("You're offline. The photo is saved; type in the details by hand.");
          }
        } else {
          toast.warning("You're offline. The photo is saved; type in the details by hand.");
        }
      } else {
        // The photo is stored before the model runs, so a failed read still
        // leaves the rep something to work from. Say so rather than failing
        // silently: losing the lead is the worst outcome (hard rule 4).
        const frontKey = err.badge_image_key || err.data?.badge_image_key;
        if (frontKey) onImageCaptured(frontKey);
        const backKey = err.badge_back_image_key || err.data?.badge_back_image_key;
        if (backKey && onBackImageCaptured) onBackImageCaptured(backKey);
        toast.error(err.message || 'Could not read the badge. Type the details in.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCapture = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // A fresh photo makes any earlier tap-to-fill panel stale: it was
    // reading blocks from the previous photo, not this one.
    setOnDeviceBlocks(null);
    setIsProcessing(true);
    setProcessingLabel('Compressing...');
    const compressed = await compressImage(file);
    setFrontFile(compressed);
    await runScan(compressed, null);
  };

  const handleBackCapture = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => setBackPreview(e.target.result);
    reader.readAsDataURL(file);

    setIsProcessing(true);
    setProcessingLabel('Compressing...');
    const compressed = await compressImage(file);
    await runScan(frontFile, compressed);
  };

  const handleRescan = () => {
    setPreview(null);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const handleRemoveBack = () => {
    setBackPreview(null);
    if (onBackImageCaptured) onBackImageCaptured(null);
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          handleCapture(e.target.files[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={backFileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          handleBackCapture(e.target.files[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={libraryFileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files[0];
          if (libraryTarget === 'back') handleBackCapture(file);
          else handleCapture(file);
          e.target.value = '';
        }}
      />

      {preview ? (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border-2 border-accent/30 bg-black">
            <img
              src={preview}
              alt="Scan"
              className="w-full h-48 object-contain cursor-zoom-in"
              onClick={() => !isProcessing && setLightbox('front')}
            />
            {isProcessing && (
              <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center text-primary-foreground">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm font-medium">{processingLabel}</p>
                </div>
              </div>
            )}
            {!isProcessing && (
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur rounded-full hover:bg-background transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {!isProcessing && onDeviceBlocks && (
            <TapToFillBlocks
              blocks={onDeviceBlocks}
              onAssign={(field, value) => onDataExtracted({ [field]: value })}
              onDismiss={() => setOnDeviceBlocks(null)}
            />
          )}

          {backPreview && (
            <div className="relative rounded-xl overflow-hidden border-2 border-accent/30 bg-black">
              <img
                src={backPreview}
                alt="Back of card"
                className="w-full h-32 object-contain cursor-zoom-in"
                onClick={() => !isProcessing && setLightbox('back')}
              />
              <span className="absolute bottom-2 left-2 text-[10px] font-medium bg-background/80 backdrop-blur px-1.5 py-0.5 rounded">
                Back
              </span>
              {!isProcessing && (
                <button
                  onClick={handleRemoveBack}
                  className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur rounded-full hover:bg-background transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {!isProcessing && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleRescan}
                className="flex-1 min-h-10 text-sm font-semibold border-accent/50 text-accent hover:bg-accent/10"
              >
                <Camera className="w-4 h-4 mr-2" />
                Rescan Info
              </Button>
              {!backPreview && (
                <Button
                  variant="outline"
                  onClick={() => backFileInputRef.current?.click()}
                  className="flex-1 h-10 text-sm font-semibold border-muted-foreground/30 text-muted-foreground hover:bg-secondary/40"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Back of Card
                </Button>
              )}
            </div>
          )}
          {!isProcessing && !backPreview && (
            <button
              onClick={() => { setLibraryTarget('back'); libraryFileInputRef.current?.click(); }}
              className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
            >
              Choose back of card from library instead
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-accent/40 rounded-xl p-8 flex flex-col items-center gap-3 hover:border-accent hover:bg-accent/5 transition-all group"
          >
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
              <Camera className="w-7 h-7 text-accent" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">Scan Badge or Card</p>
              <p className="text-sm text-muted-foreground mt-0.5">Take a photo to auto-fill details</p>
            </div>
          </button>
          <button
            onClick={() => { setLibraryTarget('front'); libraryFileInputRef.current?.click(); }}
            className="w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
          >
            Choose from library instead
          </button>
        </div>
      )}

      {/* Lightbox, front or back depending on which preview was tapped */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              onClick={() => setLightbox(null)}
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <motion.img
              src={lightbox === 'back' ? backPreview : preview}
              alt="Full size scan"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-full max-h-full rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
